import React, { useState } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type {
	ActionCallbacks,
	ActionStatusProps,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import type { UpdateWorkItemFieldsAction } from '../../../common/types';

import type { actionCard_updateWorkItemFieldsActionRenderer$key } from './__generated__/actionCard_updateWorkItemFieldsActionRenderer.graphql';
import { CardFieldsList } from './card-fields-list';
import { CardHeader } from './card-header';

export type Props = {
	cloudId: string;
	queryRef: actionCard_updateWorkItemFieldsActionRenderer$key;
	actions: UpdateWorkItemFieldsAction[];
	shouldExpand?: boolean;
	showButtons?: boolean;
} & ActionCallbacks &
	ActionStatusProps;

const styles = cssMap({
	fieldsList: {
		paddingTop: token('space.050'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.050'),
	},
});

/**
 * Renders an action card for updating Jira issue fields within the conversation assistant.
 *
 * @remarks
 * This component displays a card containing a header and a list of field update actions.
 * It uses Relay fragments to fetch the necessary Jira issue data and passes it to child components.
 *
 * @param queryRef - The Relay fragment reference for the Jira issue data.
 * @param actions - An array of action objects representing field updates to be rendered.
 * @param props - Additional props to be passed to the CardFieldsList component.
 * @param shouldOpen - Whether the card is expanded on render or not.
 *
 * @returns
 * Returns a Stack containing the card header and a list of field update actions.
 * If no data is available or there are no actions, returns null.
 */
export const ActionCard = ({
	queryRef,
	actions,
	cloudId,
	shouldExpand = false,
	showButtons = true,
	...props
}: Props): React.JSX.Element | null => {
	di(CardHeader, CardFieldsList);

	const [isExpanded, setIsExpanded] = useState(shouldExpand);

	const data = useFragment(
		graphql`
			fragment actionCard_updateWorkItemFieldsActionRenderer on JiraIssue
			@argumentDefinitions(fieldIdsOrAliases: { type: "[String!]!" }) {
				...cardHeader_updateWorkItemFieldsActionRenderer
				...cardFieldsList_updateWorkItemFieldsActionRenderer
					@arguments(fieldIdsOrAliases: $fieldIdsOrAliases)
			}
		`,
		queryRef,
	);

	if (!data || actions.length === 0) {
		return null;
	}

	return (
		<Stack testId="update-work-item-fields-action-renderer.ui.action-card">
			<CardHeader
				queryRef={data}
				isExpanded={isExpanded}
				showButtons={showButtons}
				onClick={() => setIsExpanded(!isExpanded)}
			/>
			{isExpanded && (
				<Box xcss={styles.fieldsList}>
					{actions.map((action) => (
						<CardFieldsList
							key={action.data.fieldKey}
							queryRef={data}
							action={action}
							cloudId={cloudId}
							showActionButtons={showButtons}
							{...props}
						/>
					))}
				</Box>
			)}
		</Stack>
	);
};
